import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  FlexContainer, 
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader
} from '../../styles/StyledComponents';
import apiService from '../../services/api';

const AnalysisContainer = styled.div`
  margin-bottom: 2rem;
`;

const SummaryCard = styled.div<{ variant?: 'positive' | 'negative' | 'neutral' }>`
  background: white;
  border-radius: var(--border-radius);
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  border-left: 4px solid ${props => {
    switch (props.variant) {
      case 'positive': return 'var(--success-color)';
      case 'negative': return 'var(--danger-color)';
      default: return 'var(--primary-color)';
    }
  }};
  
  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--secondary-color);
    margin-bottom: 1rem;
  }
  
  .amount {
    font-size: 2rem;
    font-weight: 700;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
  }
  
  .description {
    font-size: 0.9rem;
    color: var(--dark-gray);
  }
`;

const CashFlowTable = styled.div`
  background: white;
  border-radius: var(--border-radius);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--box-shadow);
  
  .table-title {
    color: var(--secondary-color);
    border-bottom: 2px solid var(--light-gray);
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  .table-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--light-gray);
    
    &:last-child {
      border-bottom: none;
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1.1rem;
      background: var(--light-gray);
      padding: 1rem;
      border-radius: var(--border-radius);
      margin-top: 0.5rem;
    }
    
    .row-label {
      font-weight: 500;
      color: var(--dark-gray);
    }
    
    .row-value {
      font-weight: 600;
    }
    
    .positive {
      color: var(--success-color);
    }
    
    .negative {
      color: var(--danger-color);
    }
  }
`;

const TimelineSection = styled.div`
  background: white;
  border-radius: var(--border-radius);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--box-shadow);
  
  .timeline-title {
    color: var(--secondary-color);
    border-bottom: 2px solid var(--light-gray);
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  .timeline-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--light-gray);
    
    &:last-child {
      border-bottom: none;
    }
    
    .timeline-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 1rem;
      font-size: 1.2rem;
      color: white;
    }
    
    .timeline-content {
      flex: 1;
    }
    
    .timeline-title {
      font-weight: 600;
      color: var(--secondary-color);
      margin-bottom: 0.25rem;
    }
    
    .timeline-description {
      font-size: 0.9rem;
      color: var(--dark-gray);
    }
    
    .timeline-amount {
      font-weight: 600;
      font-size: 1.1rem;
    }
  }
`;

const CashFlowAnalysis: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [cashFlowData, setCashFlowData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCashFlowData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch project details
        const projectResponse = await apiService.getProject(projectId);
        if (!projectResponse.success || !projectResponse.data) {
          throw new Error('Failed to fetch project data');
        }
        const project = projectResponse.data;

        // Fetch payments for this project
        const paymentsResponse = await apiService.getPayments({ projectId });
        const payments = paymentsResponse.data || [];

        // Fetch expenses for this project
        const expensesResponse = await apiService.getExpenses({ projectId });
        const expenses = expensesResponse.data || [];

        // Calculate simple cash flow
        const totalIncome = payments.reduce((sum: number, payment: any) => {
          return payment.status === 'Approved' || payment.status === 'Completed' ? sum + payment.amount : sum;
        }, 0);

        const totalExpenses = expenses.reduce((sum: number, expense: any) => {
          return expense.status === 'Approved' || expense.status === 'Recorded' ? sum + expense.amount : sum;
        }, 0);

        const currentBalance = totalIncome - totalExpenses;

        // Calculate expenses by category
        const expensesByCategory = expenses.reduce((acc: any, expense: any) => {
          if (expense.status === 'Approved' || expense.status === 'Recorded') {
            if (!acc[expense.category]) {
              acc[expense.category] = 0;
            }
            acc[expense.category] += expense.amount;
          }
          return acc;
        }, {});

        // Calculate payments by type
        const paymentsByType = payments.reduce((acc: any, payment: any) => {
          if (payment.status === 'Approved' || payment.status === 'Completed') {
            if (!acc[payment.paymentType]) {
              acc[payment.paymentType] = 0;
            }
            acc[payment.paymentType] += payment.amount;
          }
          return acc;
        }, {});

        const cashFlowData = {
          project,
          totalIncome,
          totalExpenses,
          currentBalance,
          expensesByCategory,
          paymentsByType,
          payments: payments.filter((p: any) => p.status === 'Approved' || p.status === 'Completed'),
          expenses: expenses.filter((e: any) => e.status === 'Approved' || e.status === 'Recorded')
        };

        setCashFlowData(cashFlowData);

      } catch (error: any) {
        console.error('Error fetching cash flow data:', error);
        setError('Failed to load cash flow data');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchCashFlowData();
    }
  }, [projectId]);

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading cash flow analysis...</div>;
  }

  if (error) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--danger-color)' }}>{error}</div>;
  }

  if (!cashFlowData) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>No cash flow data available for this project.</div>;
  }

  return (
    <AnalysisContainer>
      <CardHeader>
        <FlexContainer justify="space-between" align="center">
          <h3>Simple Cash Flow Analysis</h3>
        </FlexContainer>
      </CardHeader>

      {/* Summary Cards */}
      <Grid columns="repeat(auto-fit, minmax(250px, 1fr))" gap="1rem" style={{ marginBottom: '2rem' }}>
        <SummaryCard variant="positive">
          <div className="card-title">💰 Total Money Received</div>
          <div className="amount">{formatCurrency(cashFlowData.totalIncome)}</div>
          <div className="description">All approved payments for this project</div>
        </SummaryCard>

        <SummaryCard variant="negative">
          <div className="card-title">💸 Total Money Spent</div>
          <div className="amount">{formatCurrency(cashFlowData.totalExpenses)}</div>
          <div className="description">All recorded expenses for this project</div>
        </SummaryCard>

        <SummaryCard variant={cashFlowData.currentBalance >= 0 ? "positive" : "negative"}>
          <div className="card-title">💳 Current Balance</div>
          <div className="amount">{formatCurrency(cashFlowData.currentBalance)}</div>
          <div className="description">
            {cashFlowData.currentBalance >= 0 
              ? 'Money available for project use' 
              : 'Project is over budget'}
          </div>
        </SummaryCard>
      </Grid>

      {/* Simple Cash Flow Table */}
      <CashFlowTable>
        <div className="table-title">📊 Cash Flow Summary</div>
        
        <div className="table-row">
          <div className="row-label">Starting Balance</div>
          <div className="row-value">TZS 0</div>
        </div>
        
        <div className="table-row">
          <div className="row-label">➕ Money Received</div>
          <div className="row-value positive">+{formatCurrency(cashFlowData.totalIncome)}</div>
        </div>
        
        <div className="table-row">
          <div className="row-label">➖ Money Spent</div>
          <div className="row-value negative">-{formatCurrency(cashFlowData.totalExpenses)}</div>
        </div>
        
        <div className="table-row">
          <div className="row-label">Current Balance</div>
          <div className={`row-value ${cashFlowData.currentBalance >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(cashFlowData.currentBalance)}
          </div>
        </div>
      </CashFlowTable>

      {/* Money Received Breakdown */}
      <CashFlowTable>
        <div className="table-title">📥 Money Received Breakdown</div>
        
        {Object.keys(cashFlowData.paymentsByType).length > 0 ? (
          Object.entries(cashFlowData.paymentsByType).map(([type, amount]: [string, any]) => (
            <div key={type} className="table-row">
              <div className="row-label">{type} Payment</div>
              <div className="row-value positive">+{formatCurrency(amount)}</div>
            </div>
          ))
        ) : (
          <div className="table-row">
            <div className="row-label">No payments received yet</div>
            <div className="row-value">TZS 0</div>
          </div>
        )}
        
        <div className="table-row">
          <div className="row-label">Total Received</div>
          <div className="row-value positive">+{formatCurrency(cashFlowData.totalIncome)}</div>
        </div>
      </CashFlowTable>

      {/* Money Spent Breakdown */}
      <CashFlowTable>
        <div className="table-title">📤 Money Spent Breakdown</div>
        
        {Object.keys(cashFlowData.expensesByCategory).length > 0 ? (
          Object.entries(cashFlowData.expensesByCategory).map(([category, amount]: [string, any]) => (
            <div key={category} className="table-row">
              <div className="row-label">{category}</div>
              <div className="row-value negative">-{formatCurrency(amount)}</div>
            </div>
          ))
        ) : (
          <div className="table-row">
            <div className="row-label">No expenses recorded yet</div>
            <div className="row-value">TZS 0</div>
          </div>
        )}
        
        <div className="table-row">
          <div className="row-label">Total Spent</div>
          <div className="row-value negative">-{formatCurrency(cashFlowData.totalExpenses)}</div>
        </div>
      </CashFlowTable>

      {/* Recent Activity Timeline */}
      <TimelineSection>
        <div className="timeline-title">📅 Recent Activity</div>
        
        {[...cashFlowData.payments, ...cashFlowData.expenses]
          .sort((a: any, b: any) => new Date(b.createdAt || b.recordedAt).getTime() - new Date(a.createdAt || a.recordedAt).getTime())
          .slice(0, 10)
          .map((item: any, index: number) => (
            <div key={index} className="timeline-item">
              <div 
                className="timeline-icon" 
                style={{ 
                  background: item.amount ? 'var(--success-color)' : 'var(--danger-color)'
                }}
              >
                {item.amount ? '💰' : '💸'}
              </div>
              <div className="timeline-content">
                <div className="timeline-title">
                  {item.amount ? 'Payment Received' : 'Expense Recorded'}
                </div>
                <div className="timeline-description">
                  {item.amount ? item.description : item.description}
                  <br />
                  {formatDate(new Date(item.createdAt || item.recordedAt))}
                </div>
              </div>
              <div className={`timeline-amount ${item.amount ? 'positive' : 'negative'}`}>
                {item.amount ? `+${formatCurrency(item.amount)}` : `-${formatCurrency(item.amount)}`}
              </div>
            </div>
          ))}
        
        {cashFlowData.payments.length === 0 && cashFlowData.expenses.length === 0 && (
          <div className="timeline-item">
            <div className="timeline-content">
              <div className="timeline-title">No activity yet</div>
              <div className="timeline-description">No payments or expenses have been recorded for this project.</div>
            </div>
          </div>
        )}
      </TimelineSection>
    </AnalysisContainer>
  );
};

export default CashFlowAnalysis; 